import React from 'react'

import { CopyInput } from '@components/common'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  Banner,
  Button,
  ButtonClean,
  Checkbox,
  Fieldset,
  Icon,
  InputUI,
  Subsection,
} from '@fourthwall/components'
import { SpotifyIcon } from '@fourthwall/icons'
import { TagSelect } from '@modules/Dashboard/Memberships/components/TagSelect'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'

import { usePodcastContext } from '../../hooks/usePodcastContext'
import { SyncedBar } from '../SyncedBar/SyncedBar'
import { SyncingBar } from '../SyncingBar/SyncingBar'
import { Video } from '../Video/Video'
import * as S from './ExistingFields.styled'

export const ExistingFields = () => {
  const {
    podcast,
    tags,
    selectedTags,
    form,
    isLoading,
    handleAddTag,
    handleRemoveTag,
    handleSyncPodcast,
    handleDesyncPodcast,
  } = usePodcastContext()

  if (!podcast) return null

  return (
    <Subsection title="Add members-only audio to this podcast">
      <Video />
      <div style={{ marginBottom: 16 }}>
        Follow the steps below to allow your members to listen to members-only audio on Spotify:
      </div>
      <S.List>
        <li>
          <S.ListItem>
            Sync your members-only audio to Spotify.
            {podcast.status === 'Created' && (
              <Button
                appearance="secondary"
                leftIcon={<Icon component={SpotifyIcon} height={20} />}
                iconColor="#1ed760"
                label="Sync podcast to Spotify"
                loading={isLoading}
                onClick={handleSyncPodcast}
              />
            )}
            {podcast.status === 'Syncing' && <SyncingBar loading={isLoading} />}
            {podcast.status === 'Synced' && (
              <SyncedBar
                url={podcast.spotifyUrl || ''}
                loading={isLoading}
                onClick={handleDesyncPodcast}
              />
            )}
            {podcast.status === 'Errored' && (
              <SyncedBar
                url={podcast.spotifyUrl || ''}
                errored
                loading={isLoading}
                onClick={handleDesyncPodcast}
              />
            )}
            <div style={{ width: '100%' }}>
              <Fieldset>
                {podcast.status === 'Errored' && podcast.ingestionValidationErrors && (
                  <Fieldset.Row>
                    <Banner appearance="danger" title={podcast.ingestionValidationErrors} />
                  </Fieldset.Row>
                )}
                <Fieldset.Row>
                  <Checkbox
                    label="Sync only audio posts with specific tags to Spotify"
                    {...form.getFieldProps('withTags')}
                  />
                </Fieldset.Row>
                {form.values.withTags && (
                  <Fieldset.Row inner>
                    <S.TagSelectContainer>
                      <TagSelect
                        label={
                          tags.length === 0 ? 'You haven’t created any tags yet' : 'Select tags'
                        }
                        selectedTags={selectedTags}
                        tags={tags}
                        onAdd={handleAddTag}
                        onRemove={handleRemoveTag}
                      />
                      {form.errors.tags && (
                        <InputUI.Error>{form.errors.tags.toString()}</InputUI.Error>
                      )}
                    </S.TagSelectContainer>
                  </Fieldset.Row>
                )}
              </Fieldset>
            </div>
          </S.ListItem>
        </li>
        <li>
          Go to{' '}
          <ButtonClean
            label="Spotify for Creators"
            withUnderline
            href="https://creators.spotify.com/"
            target="_blank"
          />{' '}
          and login to the Spotify account your main podcast is under.
        </li>
        <li>
          Click the small circle account icon in the top right of the page and select{' '}
          <S.Bold>Add a new show</S.Bold>.
        </li>
        <li>
          Select <S.Bold>Find an existing show</S.Bold>, then choose that podcast is hosted{' '}
          <S.Bold>Somewhere else</S.Bold>.
        </li>
        <li>
          <S.ListItem>
            Copy the link below and paste it into “RSS feed link” field.
            <CopyInput
              label="Members-only audio RSS link"
              buttonLabel="Copy URL"
              content={podcast.rssUrl || ''}
            />
          </S.ListItem>
        </li>
        <li>You should now see your members-only audio on your Spotify account.</li>
        <li>
          Select your main podcast from the <S.Bold>Your shows</S.Bold> menu on the left.
        </li>
        <li>
          Click <S.Bold>Settings</S.Bold> on the left menu.
        </li>
        <li>
          Scroll down to the <S.Bold>Synced feeds</S.Bold> section and click{' '}
          <S.Bold>Learn more</S.Bold> and then <S.Bold>Get started</S.Bold>.
        </li>
        <li>
          On the <S.Bold>Link your feeds</S.Bold> page, select your podcast in the{' '}
          <S.Bold>Paid feed</S.Bold> dropdown and click <S.Bold>Next</S.Bold>.
        </li>
        <li>
          Select your perks to complete the process! Your members-only audio should now show up
          under your podcast.
        </li>
      </S.List>
      <ConfirmBar
        isOpen={form.dirty}
        isLoading={isLoading}
        onConfirm={form.submitForm}
        onCancel={form.resetForm}
      />
      <LeavingGuard when={form.dirty} isValid={form.isValid} onSave={form.submitForm} />
    </Subsection>
  )
}
